import React, { useState } from 'react';
import { format, getISOWeek, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Datumsfeld mit Kalenderwochen.
 *
 * Das eingebaute Datumsfeld des Browsers (input type="date") kann keine
 * Kalenderwochen anzeigen – das Aussehen bestimmt der Browser, nicht die
 * Anwendung. Deshalb ein eigener Kalender: links die KW-Spalte wie auf dem
 * Wandkalender im Büro, und im Feld selbst steht nach der Auswahl zusätzlich
 * die Woche. Bei Projekten, die über Monate laufen, wird im Betrieb in
 * Kalenderwochen gesprochen, nicht in Datumsangaben.
 *
 * Gezählt wird nach ISO 8601 – dieselbe Zählung wie auf dem Wandkalender und
 * in der Plantafel. Eine zweite Zählweise im selben Haus wäre eine
 * Fehlerquelle, die niemand bemerkt, bis sich zwei Leute um eine Woche
 * verpassen.
 */
export default function DatumFeld({
  value, onChange, disabled = false, placeholder = 'Datum wählen', testId,
}) {
  const [offen, setOffen] = useState(false);

  const datum = value ? parseISO(value) : null;
  const gueltig = datum && isValid(datum);

  const beschriftung = gueltig
    ? `${format(datum, 'dd.MM.yyyy', { locale: de })} · KW ${getISOWeek(datum)}`
    : placeholder;

  return (
    <Popover open={offen} onOpenChange={setOffen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`w-full justify-start font-normal ${gueltig ? '' : 'text-slate-400'}`}
          data-testid={testId}
        >
          <CalendarDays size={14} className="mr-2 flex-shrink-0 text-slate-500" />
          {beschriftung}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={de}
          // ISOWeek: Montag ist der erste Tag, und die Zählung entspricht dem
          // Wandkalender. Ohne das beginnt die Woche am Sonntag und die
          // Nummern weichen ab.
          ISOWeek
          showWeekNumber
          selected={gueltig ? datum : undefined}
          defaultMonth={gueltig ? datum : new Date()}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, 'yyyy-MM-dd'));
            setOffen(false);
          }}
        />
        <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
          Linke Spalte: Kalenderwoche
        </div>
      </PopoverContent>
    </Popover>
  );
}
